import {useTheme} from '@mui/material/styles'
import type {FC, ReactElement} from 'react'
import {useAnomalyStatusColor} from '@hooks/GetAnomalyStatusHook'
import type {ProductionTimelineAnomalyInterface} from '@interfaces/anomaly'
import {calculateTime} from '@app/util-functions'

interface D3ProductionTimelineRectProps {
  keyProp: string
  x: number
  y: number
  width: number
  rectHeight: number
  fill: string
  hours: number
  onMouseEnter: () => void
  onMouseLeave: () => void
  onClick?: (value: string) => void
  mainKey: string
  anomalies?: ProductionTimelineAnomalyInterface[]
  vertical?: boolean
}

const ProductionTimelineRect: FC<D3ProductionTimelineRectProps> = ({
  keyProp,
  x,
  y,
  width,
  rectHeight,
  fill,
  hours,
  onMouseEnter,
  onMouseLeave,
  onClick,
  mainKey,
  anomalies,
  vertical,
}): ReactElement => {
  // Hooks
  const theme = useTheme()
  const {getAnomalyStatusColor} = useAnomalyStatusColor()

  const handleMouseEnter = (): void => {
    onMouseEnter()
  }

  const handleMouseLeave = (): void => {
    onMouseLeave()
  }

  // Calculate the center position for the text
  const textX = x + width / 2
  const textY = y + rectHeight / 2 // Adjust 5 for vertical alignment

  const getIconIsValidAnomalyValidation = (
    anomalies: ProductionTimelineAnomalyInterface[] | undefined,
  ): boolean => {
    if (!anomalies) {
      return false
    }
    return anomalies.some((anomaly) => anomaly.is_valid_anomaly === true)
  }

  const getBorderColorByStatus = (
    anomalies: ProductionTimelineAnomalyInterface[] | undefined,
  ): string => {
    if (!anomalies) {
      return ''
    }

    const allIsValid = anomalies.map((anomaly) => anomaly.is_valid_anomaly)

    if (allIsValid.includes(true)) {
      return getAnomalyStatusColor('red')
    }
    if (allIsValid.includes(null)) {
      return getAnomalyStatusColor('orange')
    }

    return getAnomalyStatusColor('green')
  }

  const MAX_WIDTH_FOR_VERTICAL = 60
  const MIN_HEIGHT_FOR_VERTICAL = 35

  const shouldRenderVertical =
    width < MAX_WIDTH_FOR_VERTICAL && rectHeight > MIN_HEIGHT_FOR_VERTICAL

  return (
    <g
      onClick={() => (onClick ? onClick(mainKey) : null)}
      style={{cursor: onClick ? 'pointer' : 'default'}}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <rect
        key={keyProp}
        x={x}
        y={y}
        width={width}
        height={rectHeight}
        fill={fill}
        rx="3"
        ry="3"
        stroke={
          anomalies && anomalies?.length > 0 ? getBorderColorByStatus(anomalies) : 'rgba(0,0,0,.5)'
        }
        strokeWidth={anomalies && anomalies.length > 0 ? 4 : 1}
        data-cy="mbai-production-timeline-rect"
      />
      <text
        x={textX}
        y={textY}
        textAnchor="middle"
        fill={
          fill === 'rgba(0, 0, 0, 0.38)ff' ? 'rgba(255, 255, 255,.9)' : theme.palette.text.secondary
        } // Text color
        fontSize={width > 30 ? '10px' : width <= 30 && width > 20 ? '8px' : '6px'} // Adjust as necessary
        dy=".35em" // To center the text vertically
        transform={
          shouldRenderVertical || vertical
            ? `rotate(-90, ${x + width / 2}, ${y + rectHeight / 2})`
            : undefined
        }
        style={{pointerEvents: 'none'}}
      >
        {width > 10 ? calculateTime(hours) : null}
      </text>
      {getIconIsValidAnomalyValidation(anomalies) && width > 30 ? (
        <image
          href="/static/warning-in-triangle-symbol-and-sign-png.webp" // Specify the image source path here
          x={x + 5} // Set the x position of the image
          y={y + 5} // Set the y position of the image
          width={15} // Set the width of the image
          height={15} // Set the height of the image
        />
      ) : null}
    </g>
  )
}

export default ProductionTimelineRect
